import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, Ban, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";
import { ExtratoDocumento, type ExtratoParaDocumento } from "@bookdental/ui";
import { FORMA_DE_PAGAMENTO_LABELS, SITUACAO_DO_EXTRATO_LABELS } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { reais, type ExtratoAberto } from "@/lib/dinheiro";
import { useSetPageHeader } from "@/lib/page-header";
import { usePermissao } from "@/lib/use-permissao";
import { BotaoExportar } from "@/components/shared/BotaoExportar";
import { RegistrarRecebimento } from "@/components/financeiro/a-receber/RegistrarRecebimento";
import { BotaoDarComoPerdido } from "@/components/financeiro/a-receber/PerdaDialog";
import { COR_DA_SITUACAO_DO_EXTRATO, dataCurta, mesDoExtrato } from "@/components/financeiro/extratos/situacao";
import { EnvioDoExtrato } from "@/components/cobrancas/EnvioDoExtrato";
import { useParametroDaUrl } from "@/lib/use-parametro-da-url";

// Na impressão só o documento aparece: menu, barra, botões e o painel ao lado somem.
const CSS_DE_IMPRESSAO = `
@media print {
  body * { visibility: hidden !important; }
  [data-extrato-impresso], [data-extrato-impresso] * { visibility: visible !important; }
  [data-extrato-impresso] { position: absolute; inset: 0 auto auto 0; width: 100%; padding: 0 !important; border: 0 !important; box-shadow: none !important; }
  @page { margin: 14mm; }
}`;

type Item = ExtratoAberto["itens"][number];

/** O extrato emitido: o documento (que vira PDF), a planilha detalhada, os recebimentos e o
 *  cancelamento — corrigir é cancelar e emitir outro. */
export function ExtratoVisualizar() {
  const { id = "" } = useParams();
  const utils = trpc.useUtils();
  const podeMexer = usePermissao("finance.manage");
  const consulta = trpc.financeiro.extratos.abrir.useQuery({ id });
  const e = consulta.data;
  // Vindo dos três pontinhos da lista: ?imprimir=1 abre a impressão, ?receber=1 leva ao recebimento.
  const [pedido, setPedido] = useState<"imprimir" | "receber" | null>(null);
  useParametroDaUrl("imprimir", () => setPedido("imprimir"));
  useParametroDaUrl("receber", () => setPedido("receber"));
  useEffect(() => {
    if (!pedido || !e) return;
    // Sem limpar o timer: o setPedido(null) logo abaixo re-renderiza e cancelaria a ação.
    setTimeout(() => {
      if (pedido === "imprimir") window.print();
      else document.querySelector<HTMLInputElement>("[data-registrar-recebimento] input")?.focus();
    }, 400);
    setPedido(null);
  }, [pedido, e]);
  useSetPageHeader({ title: e ? `Extrato nº ${e.numero}` : "Extrato", subtitle: e?.destinatarioNome }, [e?.numero, e?.destinatarioNome]);

  const cancelar = trpc.financeiro.extratos.cancelar.useMutation({
    onSuccess: () => {
      utils.financeiro.invalidate();
      toast.success("Extrato cancelado. Os itens voltaram para A faturar.");
    },
    onError: (err) => toast.error(err.message),
  });

  if (consulta.isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
      </div>
    );
  }
  if (!e) return <p className="py-24 text-center text-sm text-zinc-500">Extrato não encontrado.</p>;

  const documento: ExtratoParaDocumento = { ...e, cabecalho: (e.cabecalho ?? {}) as ExtratoParaDocumento["cabecalho"] };
  const pedirCancelamento = () => {
    const motivo = window.prompt(`Cancelar o extrato nº ${e.numero}? Os itens voltam para "A faturar". Motivo (opcional):`);
    if (motivo === null) return;
    cancelar.mutate({ id: e.id, motivo });
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      <style>{CSS_DE_IMPRESSAO}</style>
      <div className="flex flex-wrap items-center gap-2">
        <Link to="/financeiro?aba=receber&ver=extratos" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />Cobranças
        </Link>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {podeMexer && e.status === "EMITIDO" && e.recebidoCents === 0 && (
            <button
              type="button"
              onClick={pedirCancelamento}
              disabled={cancelar.isPending}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-red-200 px-3 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-500/30 dark:hover:bg-red-500/10"
            >
              <Ban className="h-4 w-4" />
              Cancelar extrato
            </button>
          )}
          <BotaoExportar
            nome={`extrato-${e.numero}-${e.destinatarioNome.toLowerCase().replace(/\s+/g, "-")}`}
            titulo="extrato"
            tela="extrato"
            planilha="extrato"
            quantidade={e.itens.length}
            idDe={(i: Item) => i.id}
            buscarLinhas={async () => e.itens}
            colunas={[
              { titulo: "Data de entrega", valor: (i) => dataCurta(i.data) },
              { titulo: "Pedido", valor: (i) => i.pedidoNumero },
              { titulo: "Paciente", valor: (i) => i.paciente },
              { titulo: "Dentista", valor: (i) => i.dentista },
              { titulo: "Serviço", valor: (i) => i.descricao },
              { titulo: "Material", valor: (i) => i.material },
              { titulo: "Dentes", valor: (i) => i.dentes },
              { titulo: "Unidades", valor: (i) => i.quantidade },
              { titulo: "Valor unitário", valor: (i) => (i.valorUnitarioCents == null ? null : i.valorUnitarioCents / 100) },
              { titulo: "Valor total", valor: (i) => i.valorCents / 100 },
            ]}
          />
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
          >
            <Printer className="h-4 w-4" />
            Baixar PDF
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_320px] lg:items-start">
        <div data-extrato-impresso className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm sm:p-10">
          <ExtratoDocumento extrato={documento} />
        </div>

        <aside className="space-y-3 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-border dark:bg-card">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">Mês {mesDoExtrato(e.rotulo)}</p>
            <span className={cn("rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase", COR_DA_SITUACAO_DO_EXTRATO[e.situacao])}>
              {SITUACAO_DO_EXTRATO_LABELS[e.situacao]}
            </span>
          </div>
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between"><dt className="text-zinc-500">Total</dt><dd className="font-semibold tabular-nums">{reais(e.totalCents)}</dd></div>
            <div className="flex justify-between"><dt className="text-zinc-500">Recebido</dt><dd className="font-semibold tabular-nums text-emerald-600">{reais(e.recebidoCents)}</dd></div>
            {e.perdidoCents > 0 && <div className="flex justify-between"><dt className="text-zinc-500">Dado como perdido</dt><dd className="font-semibold tabular-nums text-red-600">{reais(e.perdidoCents)}</dd></div>}
            <div className="flex justify-between border-t border-zinc-100 pt-1 dark:border-border"><dt className="text-zinc-500">Saldo</dt><dd className="font-bold tabular-nums">{reais(Math.max(0, e.saldoCents))}</dd></div>
            <div className="flex justify-between text-xs"><dt className="text-zinc-500">Vencimento</dt><dd>{dataCurta(e.vencimento)}</dd></div>
          </dl>
          {e.recebimentos.length > 0 && (
            <ul className="space-y-1 border-t border-zinc-100 pt-2 text-xs dark:border-border">
              {e.recebimentos.map((r) => (
                <li key={r.id} className="flex justify-between gap-2 text-zinc-600 dark:text-muted-foreground">
                  <span>
                    {r.pagoEm ? dataCurta(r.pagoEm) : "—"}
                    {r.formaDePagamento ? ` · ${FORMA_DE_PAGAMENTO_LABELS[r.formaDePagamento]}` : ""}
                  </span>
                  <span className="font-medium tabular-nums">{reais(r.valorCents)}</span>
                </li>
              ))}
            </ul>
          )}
          {e.status === "CANCELADO" && (
            <p className="rounded-md bg-zinc-50 p-2 text-xs text-zinc-600 dark:bg-muted/40 dark:text-muted-foreground">
              Cancelado{e.motivoDoCancelamento ? `: ${e.motivoDoCancelamento}` : ""}. Os itens voltaram para A faturar.
            </p>
          )}
          {podeMexer && e.status === "EMITIDO" && e.saldoCents > 0 && (
            <div data-registrar-recebimento>
              <RegistrarRecebimento extratoId={e.id} saldoCents={e.saldoCents} estreito />
              <div className="mt-2 text-right">
                <BotaoDarComoPerdido saldoCents={e.saldoCents} alvo={{ extratoId: e.id }} descricao={`Extrato nº ${e.numero}, ${e.destinatarioNome}`} />
              </div>
            </div>
          )}
          {podeMexer && e.status === "EMITIDO" && <EnvioDoExtrato extrato={e} />}
          {podeMexer && e.status === "EMITIDO" && (e.recebidoCents > 0 || e.perdidoCents > 0) && (
            <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">Para cancelar, estorne antes os recebimentos e as perdas em Lançamentos.</p>
          )}
        </aside>
      </div>
    </div>
  );
}
